import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {NotificationType} from '~core/enum';
import {Activity, ActivityHistory, Customer, Pager} from '~core/models';
import {ActivityHistoryService, DefaultNotificationService, PaginationService} from '~core/services';
import {ActivityHistoryTranslationsService} from '~shared/translations';


/**
 * Handles activity history.
 */
@UntilDestroy()
@Component({
  selector: 'app-activity-history',
  templateUrl: 'activity-history.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class ActivityHistoryComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public activityHistory: Array<ActivityHistory> = [];
  public modalOpened: boolean = false;
  public selectedActivity: Activity = {
    text: ''
  };
  public itemsPerPage: number = 3;
  public pager: Pager;
  public loadingActivityHistory: boolean = false;

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('trigger') public el: any;

  constructor(
    private notificationService: DefaultNotificationService,
    private activityHistoryService: ActivityHistoryService,
    private activityHistoryTranslationsService: ActivityHistoryTranslationsService,
    private paginationService: PaginationService,
    public customerService: CustomerOverViewService
  ) {
  }

  /**
   * Fetch activity history for this customer.
   */
  public ngOnInit(): void {
    this.activityHistoryService.castActivityHistory.pipe(untilDestroyed(this))
      .subscribe(
        (data: Activity) => {
          if (data !== null) {
            this.activateActivityHistory(data);
          }
        }
      );
  }

  /**
   * Get the requested activity history.
   */
  public getActivityHistory(page: number): void {
    if (this.customer.id != null) {
      this.loadingActivityHistory = true;
      const contactId = String(this.customer.id);
      this.activityHistoryService.getCustomerHistory(
        contactId,
        Number(page),
        this.itemsPerPage
      ).pipe(untilDestroyed(this))
        .subscribe({
          next: (res: any) => {
            const totalCount = res['hydra:totalItems'];
            this.pager = this.paginationService.getPager(Number(totalCount), Number(page), this.itemsPerPage);
            this.activityHistory = res['hydra:member'];
            this.loadingActivityHistory = false;
          },
          error: (error: Error) => {
            this.setModalStatus(false);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.activityHistoryTranslationsService.i18nErrorFetchingActivityHistory
            });
            this.loadingActivityHistory = false;
          }
        });
    } else {
      this.setModalStatus(false);
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: this.activityHistoryTranslationsService.i18nErrorFetchingActivityHistory
      });
    }
  }

  /**
   * Set the activity for modal.
   */
  public setSelectedActivity(activity: Activity): void {
    this.selectedActivity = activity;
    this.getActivityHistory(1);
  }

  /**
   * Set the status of the modal and set the selected activity.
   */
  public activateActivityHistory(activity: Activity): void {
    this.setSelectedActivity(activity);
    this.setModalStatus(true);
  }

  /**
   * Set the page and call the activity history method to get the data.
   */
  public setPage(page: number): void {
    if (page < 1 || page > this.pager.totalPages) {
      return;
    }

    this.getActivityHistory(page);
  }

  /**
   * Set modal status.
   */
  public setModalStatus(status: any): void {
    this.modalOpened = status;
  }

  /**
   * Returns the translated values for apropirate key.
   */
  public getTranslatedKeyValue(value: any): void {
    return this.activityHistoryTranslationsService[value];
  }

  /**
   * Scrolls to and opens the activity history section
   */
  public scrollAndOpen(): void {
    const element: HTMLElement = this.el.nativeElement;
    if (!element.classList.contains('-is-open')) {
      element.click();
    }
    setTimeout(() => {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 300);
  }

  public ngOnDestroy(): void {
  }
}
